import { FC } from 'react';
import { DateField, DateIntervalFields, HourSelectField, SelectField } from '@mews-commander/form';
import { lt, useLocalizeText } from '@mews/framework';
import { Divider, Stack, StackItem, Typography } from '@optimus-web/core';
import { AgeCategory, OccupancyState, Resource, ResourceCategory, TimeUnitPeriod } from '@mews-commander/data-access';
import { GroupOption, Option } from '@mews-commander/types';
import { UnicodeEntity } from '@mews/enums';
import { SpacedPlusSign } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { NewReservationFormKeys } from '../schema';
import { AgeCategoriesCounterField } from '../../../../../components/form/hookFields';
import { ResourceOccupancyStateTag } from '../../../../../components';
import { NewReservationDateTimeBoundaries } from '../types';

const OMITTED_OCCUPANCY_STATUSES = [OccupancyState.Vacant];

const arrivalProps = {
    name: NewReservationFormKeys.ArrivalDate,
    required: true,
    label: lt('Arrival'),
    mode: 'utc' as const,
    size: 'medium' as const,
    'data-test-id': NewReservationFormKeys.ArrivalDate,
};

const departureProps = {
    name: NewReservationFormKeys.DepartureDate,
    required: true,
    label: lt('Departure'),
    mode: 'utc' as const,
    size: 'medium' as const,
    'data-test-id': NewReservationFormKeys.DepartureDate,
};

const hourSelectCommonProps = {
    label: UnicodeEntity.NoBreakSpace,
    size: 'medium' as const,
    showEmptyValue: false,
    disableInputSearch: true,
};

interface NewReservationFormFieldsProps {
    resource: Resource;
    resourceCategory: ResourceCategory;
    ageCategories: AgeCategory[];
    timeUnitPeriod: TimeUnitPeriod;
    loading: boolean;
    ratesOptions: Option[] | GroupOption[] | undefined;
    resourceOccupancyStates: OccupancyState[] | undefined;
    rateSelectFieldHelper: string | null | undefined;
    dateTimeBoundaries: NewReservationDateTimeBoundaries;
}

export const NewReservationFormFields: FC<NewReservationFormFieldsProps> = ({
    resource,
    resourceCategory,
    ageCategories,
    timeUnitPeriod,
    loading,
    ratesOptions,
    resourceOccupancyStates,
    rateSelectFieldHelper,
    dateTimeBoundaries: { arrivalMinDate, arrivalMaxDate, arrivalMaxHours, departureMinDate, departureMinHours },
}) => {
    const noRates = ratesOptions?.length === 0;
    const ltt = useLocalizeText();

    return (
        <Stack vertical spacing="400">
            <Stack vertical spacing="150">
                <Typography textStyle="bodyLargeStrong" as="div">
                    {lt('DatePlural')}
                </Typography>
                {timeUnitPeriod === TimeUnitPeriod.Hour ? (
                    <Stack vertical spacing="150" wrap>
                        <Stack spacing="100" distribution="space-between">
                            <StackItem width="48%">
                                <DateField {...arrivalProps} minDate={arrivalMinDate} maxDate={arrivalMaxDate} />
                            </StackItem>
                            <StackItem width="48%">
                                <HourSelectField
                                    {...hourSelectCommonProps}
                                    name={NewReservationFormKeys.ArrivalHours}
                                    data-test-id={NewReservationFormKeys.ArrivalHours}
                                    maxValue={arrivalMaxHours}
                                />
                            </StackItem>
                        </Stack>
                        <Stack spacing="100" distribution="space-between">
                            <StackItem width="48%">
                                <DateField {...departureProps} minDate={departureMinDate} loading={loading} />
                            </StackItem>
                            <StackItem width="48%">
                                <HourSelectField
                                    name={NewReservationFormKeys.DepartureHours}
                                    data-test-id={NewReservationFormKeys.DepartureHours}
                                    minValue={departureMinHours}
                                    {...hourSelectCommonProps}
                                />
                            </StackItem>
                        </Stack>
                        {resourceOccupancyStates
                            ?.filter(
                                (resourceOccupancyState) => !OMITTED_OCCUPANCY_STATUSES.includes(resourceOccupancyState)
                            )
                            .map((resourceOccupancyState) => (
                                <ResourceOccupancyStateTag occupancyState={resourceOccupancyState} />
                            ))}
                    </Stack>
                ) : (
                    <DateIntervalFields
                        inline
                        mode="utc"
                        minDayDifference={1}
                        size="medium"
                        minDate={arrivalMinDate}
                        startDateProps={{ ...arrivalProps, maxDate: arrivalMaxDate }}
                        endDateProps={{ ...departureProps, id: NewReservationFormKeys.DepartureDate, loading }}
                    />
                )}
            </Stack>

            <Divider />

            <Stack vertical spacing="150">
                <Typography textStyle="bodyLargeStrong" as="div">
                    {lt('Availability')}
                </Typography>

                <Stack distribution="space-between">
                    <Stack spacing="150">
                        {resource.name}
                        <Typography textStyle="bodyMedium" variant="secondary" as="div">
                            {lt('TimelineNewReservationAvailabilityResourceCapacity', {
                                count: resourceCategory.capacity,
                            })}
                            {!!resourceCategory.extraCapacity && (
                                <>
                                    {SpacedPlusSign}
                                    {resourceCategory.extraCapacity}
                                    {UnicodeEntity.NoBreakSpace}
                                    {ltt({ textKey: TextKey.Extra }).toLowerCase()}
                                </>
                            )}
                        </Typography>
                    </Stack>
                    {resourceOccupancyStates
                        ?.filter((resourceOccupancyState) => resourceOccupancyState !== OccupancyState.Vacant)
                        .map((resourceOccupancyState) => (
                            <ResourceOccupancyStateTag occupancyState={resourceOccupancyState} />
                        ))}
                </Stack>

                <AgeCategoriesCounterField
                    name={NewReservationFormKeys.AgeCategories}
                    ageCategories={ageCategories}
                    maxCapacity={resourceCategory.capacity + resourceCategory.extraCapacity}
                    counterWrapperProps={{ width: '48%' }}
                />

                <SelectField
                    options={ratesOptions ?? []}
                    emptyValueLabel={lt('NewReservationPanelRatesDropdownEmptyLabel')}
                    showEmptyValue={noRates}
                    readOnly={noRates}
                    emptyValueAsNull
                    size="medium"
                    label={lt('Rate')}
                    name={NewReservationFormKeys.Rate}
                    data-test-id={NewReservationFormKeys.Rate}
                    helper={rateSelectFieldHelper}
                />
            </Stack>
        </Stack>
    );
};

/**
 * in case of hourly service we want to focus departure time picker instead of
 * departure date picker
 */
export const getDepartureDatePickerId = (timeUnitPeriod: TimeUnitPeriod) =>
    timeUnitPeriod === TimeUnitPeriod.Hour
        ? NewReservationFormKeys.DepartureHours
        : NewReservationFormKeys.DepartureDate;
